<template>
  <section class="pipeline-section">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <div class="d-flex align-items-center gap-3">
        <span class="step-indicator">01</span>
        <div>
          <h2 class="h5 mb-0">Data Ingestion</h2>
          <p class="text-2xs text-muted mb-0">Choose GWAS and LD reference from uploaded files.</p>
        </div>
      </div>
      <span class="badge bg-soft-info text-info rounded-pill px-3 text-2xs">Required</span>
    </div>

    <div class="row g-4">
      <!-- GWAS -->
      <div class="col-md-6">
        <div class="bg-white border rounded-3 shadow-sm p-4">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="text-sm fw-bold">
              <i class="bi bi-file-earmark-text text-primary me-1"></i>GWAS Summary Statistics
            </div>
            <button class="btn btn-sm btn-outline-primary" @click="$emit('refresh-gwas')" :disabled="loadingGwas">
              <i class="bi bi-arrow-clockwise"></i>
            </button>
          </div>
          <div> 
            <div class="text-2xs text-muted mb-2">
              Select a GWAS summary dataset to begin analysis.
            </div>
          </div>
          <div v-if="loadingGwas" class="text-muted text-sm py-2">
            <span class="spinner-border spinner-border-sm me-2"></span>Loading...
          </div>
          <div v-else-if="gwasError" class="alert alert-danger py-2 mb-0 text-sm">
            <i class="bi bi-exclamation-triangle me-1"></i>{{ gwasError }}
          </div>

          <select
            v-else
            class="form-select"
            :value="gwasFileId || ''"
            @change="onPickGwas($event)"
          >
            <option value="" disabled>-- Select a GWAS file --</option>
            <option v-for="f in gwasFiles" :key="f.file_id" :value="f.file_id">
              {{ f.display_name || f.file_id }}
            </option>
          </select>

          <div v-if="gwasSelectedLabel" class="text-2xs text-muted mt-2">
            Selected: <b>{{ gwasSelectedLabel }}</b>
          </div>
        </div>
      </div>

      <!-- LD -->
      <div class="col-md-6">
        <div class="bg-white border rounded-3 shadow-sm p-4">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="text-sm fw-bold">
              <i class="bi bi-diagram-3 text-primary me-1"></i>LD Reference Panel
            </div>

            <div class="d-flex gap-2">
              <!-- source switch -->
              <div class="btn-group btn-group-sm" role="group" aria-label="LD source">
                <button
                  type="button"
                  class="btn"
                  :class="ldSource === 'official' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="setLdSource('official')"
                >
                  Official
                </button>

              </div>

              <!-- refresh uploaded list only -->
              <button
                class="btn btn-sm btn-outline-primary"
                @click="$emit('refresh-ld')"
                :disabled="loadingLd || ldSource !== 'uploaded'"
                title="Refresh uploaded LD list"
              >
                <i class="bi bi-arrow-clockwise"></i>
              </button>
            </div>
          </div>

          <!-- OFFICIAL MODE -->
          <div v-if="ldSource === 'official'">
            <div class="text-2xs text-muted mb-2">
              Use built-in reference panels (recommended for reproducibility).
            </div>

            <select class="form-select" :value="officialPop" @change="onPickOfficialPop($event)">
              <option v-for="p in officialPops" :key="p" :value="p">{{ p }}</option>
            </select>



            <div v-if="ldSelectedLabel" class="text-2xs text-muted mt-1">
              Selected: <b>{{ ldSelectedLabel }}</b>
            </div>
          </div>

          <!-- UPLOADED MODE -->
          <div v-else>
            <div v-if="loadingLd" class="text-muted text-sm py-2">
              <span class="spinner-border spinner-border-sm me-2"></span>Loading...
            </div>
            <div v-else-if="ldError" class="alert alert-danger py-2 mb-0 text-sm">
              <i class="bi bi-exclamation-triangle me-1"></i>{{ ldError }}
            </div>

            <select
              v-else
              class="form-select"
              :value="fileLDId || ''"
              @change="onPickLd($event)"
            >
              <option value="" disabled>-- Select an LD file --</option>
              <option v-for="f in ldFiles" :key="f.file_id" :value="f.file_id">
                {{ f.display_name || f.file_id }}
              </option>
            </select>

            <div v-if="ldSelectedLabel" class="text-2xs text-muted mt-2">
              Selected: <b>{{ ldSelectedLabel }}</b>
            </div>

            <div class="text-2xs text-muted mt-1">
              Tip: uploaded LD should be a <b>tar.gz</b> that extracts to <code>P_T/plink.bed|bim|fam</code>.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "StepDataIngestion",
  props: {
    // gwas
    loadingGwas: Boolean,
    gwasError: String,
    gwasFiles: { type: Array, default: () => [] },
    gwasFileId: [String, Number],
    gwasSelectedLabel: String,

    // ld uploaded list
    loadingLd: Boolean,
    ldError: String,
    ldFiles: { type: Array, default: () => [] },
    fileLDId: [String, Number],
    ldSelectedLabel: String,

    // ✅ 官方LD根路径（以后迁移只改这里；也可以由父组件传）
    officialLdBasePath: {
      type: String,
      default: "/pmaster/chenxingyu/chenxy/project/10algorithm/offical_ldref/LD_reference/X_Wing/PANTHER/",
    },

    // ✅ 默认官方人群
    defaultOfficialPop: { type: String, default: "EUR" },

    // ✅ 默认模式：official / uploaded
    defaultLdSource: { type: String, default: "official" },
  },

  data() {
    return {
      ldSource: "official",                 // official | uploaded
      officialPops: ["AFR", "EUR", "EAS", "SAS"],
      officialPop: "EUR",
    };
  },

  computed: {
    officialLdPath() {
      const base = String(this.officialLdBasePath || "").replace(/\/+$/, "");
      return `${base}/${this.officialPop}`;
    },
  },

  mounted() {
    const src = String(this.defaultLdSource || "official").toLowerCase();
    this.ldSource = (src === "uploaded") ? "uploaded" : "official";

    const p = String(this.defaultOfficialPop || "EUR").toUpperCase();
    this.officialPop = this.officialPops.includes(p) ? p : "EUR";

    // 如果默认就是 official，直接 emit 一次，让父组件拿到路径
    if (this.ldSource === "official") {
      this.emitOfficialLd();
    }
  },

  methods: {
    // ---- GWAS ----
    onPickGwas(e) {
      const id = e.target.value;
      const f = (this.gwasFiles || []).find((x) => String(x.file_id) === String(id));
      if (f) this.$emit("select-gwas", f);
    },

    // ---- LD source ----
    setLdSource(src) {
      this.ldSource = src;

      // 切到 official：立即把官方路径发给父组件
      if (src === "official") {
        this.emitOfficialLd();
      }
      // 切到 uploaded：不自动选（避免覆盖用户之前选项）
      // 你如果想自动保留上一次 uploaded 选择，就保持父组件 state 即可
    },

    // ---- Official LD ----
    onPickOfficialPop(e) {
      const p = String(e.target.value || "").toUpperCase();
      this.officialPop = this.officialPops.includes(p) ? p : "EUR";
      this.emitOfficialLd();
    },

    emitOfficialLd() {
      // ✅ 这里 emit 的是“路径型 LD”，父组件应把它写到 PandT.ldref_0
      // 为了兼容你已有 select-ld(f) 逻辑，这里也给出统一字段
      this.$emit("select-ld", {
        source: "official",
        population: this.officialPop,
        ldref_0: this.officialLdPath, // ✅ 关键：最终要给 WDL 的 ldref_0
        display_name: `Official ${this.officialPop}`,
      });
    },

    // ---- Uploaded LD ----
    onPickLd(e) {
      const id = e.target.value;
      const f = (this.ldFiles || []).find((x) => String(x.file_id) === String(id));
      if (f) this.$emit("select-ld", { ...f, source: "uploaded" });
    },
  },
};
</script>

<style scoped>
.step-indicator {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  background-color: #5c60f5; color: #fff;
  border-radius: 6px; font-weight: 800; font-size: 0.7rem;
}
.bg-soft-info { background: rgba(13, 202, 240, 0.15); }
</style>